'use client';
/**
 * The time window the LMS Live tab shows, shared by the "Activity over time"
 * chart and the Live Activity list (PROD-1076, PROD-1224).
 *
 * The chart used to keep its own range, defaulting to the last hour, while the
 * list showed the newest 100 events of any age. On courses and assignments
 * whose last event was over an hour old (all of them on staging and
 * production) the chart said "No data" beside a full list. Now the tab holds
 * one range, opens on the smallest preset that holds the last event, and both
 * panels explain an empty window in the same words.
 */
import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/lib/i18n';
import { parseApiDate } from '@/lib/time/parse-api-date';
import { intlLocaleFor } from '@/components/user-portal/learner-i18n';

export type TimeRangeKey = '30m' | '1h' | '6h' | '24h' | '7d';

export interface TimeRangePreset {
  labelKey: string;
  /** "No activity in the last hour." — a whole sentence per preset. */
  emptyKey: string;
  ms: number;
  interval: 'Minute' | 'Hour' | 'Day';
}

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;

/** Smallest to largest: the default is the first one that holds the last event. */
export const TIME_RANGES: Record<TimeRangeKey, TimeRangePreset> = {
  '30m': { labelKey: 'lmsLive.range.30m.label', emptyKey: 'lmsLive.range.30m.empty', ms: 30 * MINUTE, interval: 'Minute' },
  '1h': { labelKey: 'lmsLive.range.1h.label', emptyKey: 'lmsLive.range.1h.empty', ms: HOUR, interval: 'Minute' },
  '6h': { labelKey: 'lmsLive.range.6h.label', emptyKey: 'lmsLive.range.6h.empty', ms: 6 * HOUR, interval: 'Hour' },
  '24h': { labelKey: 'lmsLive.range.24h.label', emptyKey: 'lmsLive.range.24h.empty', ms: 24 * HOUR, interval: 'Hour' },
  '7d': { labelKey: 'lmsLive.range.7d.label', emptyKey: 'lmsLive.range.7d.empty', ms: 7 * 24 * HOUR, interval: 'Day' },
};

export const TIME_RANGE_KEYS = Object.keys(TIME_RANGES) as TimeRangeKey[];

export function isTimeRangeKey(value: string): value is TimeRangeKey {
  return Object.prototype.hasOwnProperty.call(TIME_RANGES, value);
}

/**
 * The smallest preset whose window holds the scope's last event; the widest
 * (7 days) when the last event is older, or there is none.
 */
export function rangeContaining(lastEventAt: string | null | undefined, now: number): TimeRangeKey {
  const at = parseApiDate(lastEventAt)?.getTime();
  if (at === undefined) return '7d';
  const age = now - at;
  return TIME_RANGE_KEYS.find((k) => age <= TIME_RANGES[k].ms) ?? '7d';
}

const SLIDE_MS = 30 * 1000;

/**
 * `{ from, to }` ending now, stepped every 30 seconds (the realtime-stats
 * refetch cadence) so the request key stays stable between steps. `active`
 * false stops the clock for a caller that is handed its window instead.
 */
export function useSlidingWindow(ms: number, active = true): { from: string; to: string } {
  const [slot, setSlot] = useState(() => Math.floor(Date.now() / SLIDE_MS));
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setSlot(Math.floor(Date.now() / SLIDE_MS)), SLIDE_MS);
    return () => clearInterval(id);
  }, [active]);
  return useMemo(() => {
    const to = (slot + 1) * SLIDE_MS;
    return { from: new Date(to - ms).toISOString(), to: new Date(to).toISOString() };
  }, [ms, slot]);
}

const DAY = 24 * HOUR;

/**
 * The viewer's calendar day of `date`, as a day number daylight saving cannot
 * skew (lib/time/activity-time.ts counts its "yesterday" the same way). The
 * viewer's own zone: an instant on an admin screen is shown in it
 * (docs/LOCALE.md §2.9).
 */
const viewerDay = (date: Date) => Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / DAY;

/**
 * "3 hours ago" / "قبل 3 ساعات" / "3 घंटे पहले" / "il y a 3 heures".
 *
 * Seconds, minutes, hours, then CALENDAR days, weeks, months and years:
 *   - under a day, the clock: "14 hours ago", across midnight too;
 *   - from a day on, the viewer's calendar: "yesterday" is the day before
 *     today, never "24 to 35 hours ago" (Monday 23:00 is two days before
 *     Wednesday 10:00, and read "yesterday" when hours were divided by 24);
 *   - from a week on, weeks, months and years, counted and rounded DOWN ("1
 *     week ago" at 13 days, "11 months ago" at 364), so a rule last run 400
 *     days ago reads "1 year ago" and not "400 days ago". They are always a
 *     count: "last week" and "last year" name a calendar period, which a
 *     number of days does not decide.
 * It was written for the Live tab's seven-day window and stopped at days; the
 * automations list, drawer and logs use it now, where a year is ordinary.
 *
 * `largest: 'day'` stops at days again, for the one sentence that is about a
 * window measured in days (LiveRangeEmpty, below).
 */
export function relativeToNow(when: Date, now: number, locale: string, largest: 'day' | 'year' = 'year'): string {
  const tag = intlLocaleFor(locale);
  const rtf = new Intl.RelativeTimeFormat(tag, { numeric: 'auto' });
  const seconds = Math.round((when.getTime() - now) / 1000);
  if (Math.abs(seconds) < 60) return rtf.format(seconds, 'second');
  const minutes = Math.round(seconds / 60);
  if (Math.abs(minutes) < 60) return rtf.format(minutes, 'minute');
  const hours = Math.round(seconds / 3600);
  const days = viewerDay(when) - viewerDay(new Date(now));
  // The same calendar day is never "yesterday": 00:10 seen at 23:50 is 24 hours ago.
  if (Math.abs(hours) < 24 || days === 0) return rtf.format(hours, 'hour');
  if (Math.abs(days) < 7 || largest === 'day') return rtf.format(days, 'day');

  const counted = new Intl.RelativeTimeFormat(tag, { numeric: 'always' });
  const direction = days < 0 ? -1 : 1;
  const span = Math.abs(days);
  if (span < 30) return counted.format(direction * Math.floor(span / 7), 'week');
  if (span < 365) return counted.format(direction * Math.min(11, Math.floor(span / 30)), 'month');
  return counted.format(direction * Math.floor(span / 365), 'year');
}

interface EmptyProps {
  rangeKey: TimeRangeKey;
  /** From the tab's status: null = the scope has no events; undefined = not known. */
  lastEventAt?: string | null;
  /** Widen to the last 7 days; offered when that window holds the last event. */
  onShowLastWeek?: () => void;
  className?: string;
}

/**
 * Why a Live panel is empty, the same in the chart and the list: the window
 * named, when the last event happened, and a way to a window that holds it.
 */
export function LiveRangeEmpty({ rangeKey, lastEventAt, onShowLastWeek, className }: EmptyProps) {
  const { t, locale } = useTranslation();
  const now = Date.now();
  const last = parseApiDate(lastEventAt);
  const canWiden =
    !!onShowLastWeek &&
    rangeKey !== '7d' &&
    last !== null &&
    now - last.getTime() <= TIME_RANGES['7d'].ms;

  return (
    <div
      className={cn('flex flex-col items-center justify-center gap-1 px-6 text-center', className)}
      data-testid="live-range-empty"
    >
      <p className="text-sm font-medium text-foreground">
        {lastEventAt === null ? t('lmsLive.empty.never') : t(TIME_RANGES[rangeKey].emptyKey)}
      </p>
      {last && (
        <p className="text-xs text-muted-foreground">
          {/* In days, whatever the age: this line answers "No activity in the
              last 7 days." in the same unit ("Last event 30 days ago."). */}
          {t('lmsLive.empty.lastEvent', { when: relativeToNow(last, now, locale, 'day') })}
        </p>
      )}
      {canWiden && (
        <Button type="button" size="sm" variant="outline" className="mt-2" onClick={onShowLastWeek}>
          {t('lmsLive.empty.showLast7Days')}
        </Button>
      )}
    </div>
  );
}
